/**
 * Pages/Panel/ReportesNomina/ReporteListaRaya.js
 *
 * Lista de Raya de VARIAS prenóminas en un solo archivo.
 *
 * La pantalla de la prenómina saca la lista de la que está abierta; aquí se eligen
 * los periodos de una tabla con casillas y sale un libro con:
 *
 *   · una hoja por prenómina, idéntica a la de la pantalla (Registro Patronal →
 *     Departamento, con total por departamento y total general);
 *   · una hoja de Concentrado que suma todas, con el desglose por departamento
 *     dentro de cada periodo;
 *   · una hoja de Concentrado por empleado: la misma lista de raya, pero con cada
 *     empleado una sola vez y sus importes sumados a lo largo de los periodos;
 *   · opcionalmente las hojas Bajas y Vacaciones, que son la misma lista acotada a
 *     los empleados dados de baja en el rango y a los que cobraron vacaciones o
 *     prima vacacional.
 *
 * El armado del Excel vive en `../PreNomina/listaRayaExcel`, compartido con la
 * pantalla de la prenómina: si se cambia el formato, cambia en los dos lados.
 */

import { Checkbox, message, PageHeader, Select, Space, Spin, Table, Tabs, Tag } from "antd";
import axios from "axios";
import React, { Component } from "react";
import { connect } from "react-redux";
import {
  Form,
  FormItem,
  RangePickerItem,
  SelectItem,
  ButtonItem,
  ErrorItem,
  dineroDisabledMask,
} from "../../../Components/Items";
import SelectDepartamentosItem from "../../../Components/SelectDepartamentosItem";
import { FileExcelOutlined } from "@ant-design/icons";
import moment from "moment";
import "moment/locale/es";
import { descargarListaRaya } from "../PreNomina/listaRayaExcel";

moment.locale("es");

const { Option } = Select;
const { TabPane } = Tabs;

// Etiquetas del estatus de la cabecera. Se muestra como dato informativo, no como
// filtro: `parcialmente_timbrada` es lo normal (basta una línea en error) y una
// nómina histórica se queda en `historica` para siempre, así que filtrar por aquí
// escondería periodos que sí tienen recibos.
const COLOR_ESTATUS = {
  borrador: "default",
  calculada: "blue",
  cerrada: "gold",
  timbrada: "green",
  parcialmente_timbrada: "orange",
  cancelada: "red",
  historica: "purple",
};

class ReporteListaRaya extends Component {
  formRef = React.createRef();

  state = {
    loading: false,
    generando: false,
    consultado: false,
    prenominas: [],
    seleccionadas: [],
    objetoRegistrosPatronales: null,
    objetoTiposPeriodo: null,
    // Hojas del libro. Las tres primeras son el reporte de siempre; Bajas y
    // Vacaciones reproducen las hojas del formato del cliente.
    hojasPorPrenomina: true,
    concentrado: true,
    concentradoEmpleados: true,
    bajas: false,
    vacaciones: false,
    // Columnas de identidad fiscal (RFC, NSS, CURP) después de "Empleado", sólo en las hojas
    // de detalle por empleado. Apagado por omisión: son datos personales y el archivo circula
    // por correo, así que el libro de siempre no cambia de forma a menos que se pidan.
    datosEmpleado: false,
    // Un finiquito se paga dentro de la prenómina ordinaria, así que sus importes de
    // liquidación inflan la raya del periodo. 'todos' | 'sin' | 'solo'.
    filtroFiniquito: "todos",
  };

  atras() {
    this.props.history.goBack();
  }

  onFinishFailed(e) {
    ErrorItem(e);
  }

  componentDidMount() {
    axios
      .post(
        "registrosPatronales/selectRegistrosPatronalesItem",
        { empresaId: this.props.empresaId },
        { headers: this.props.headersToken }
      )
      .then((res) => {
        if (res.data.success === 2) {
          this.setState({
            objetoRegistrosPatronales: (res.data.registrosPatronales || []).map((value) => (
              <Option value={value._id} key={value._id}>
                {value.registroPatronalIMSS}
              </Option>
            )),
          });
        }
      })
      .catch(() => {});

    // selectTiposPeriodosItem topa en 10, de sobra para el filtro: una empresa rara
    // vez tiene más tipos de periodo que Semanal / Catorcenal / Quincenal / Mensual.
    axios
      .post(
        "tiposPeriodos/selectTiposPeriodosItem",
        { empresaId: this.props.empresaId },
        { headers: this.props.headersToken }
      )
      .then((res) => {
        if (res.data.success !== 2) return;
        this.setState({
          objetoTiposPeriodo: (res.data.tiposPeriodos || []).map((value) => (
            <Option value={value._id} key={value._id}>
              {value.nombre}
            </Option>
          )),
        });
      })
      .catch(() => {});
  }

  // ── Paso 1: traer las prenóminas del rango ────────────────────────────────
  consultarPrenominas(values) {
    this.setState({ loading: true });
    const [fechaInicio, fechaFin] = values.periodo || [];

    axios
      .post(
        "reportesNomina/prenominasParaReporte",
        {
          empresaId: this.props.empresaId,
          // El rango va contra Prenomina.fechaPago, que es una fecha-día: se manda
          // como "YYYY-MM-DD" para que parsearFechaDia lo ancle igual sin depender
          // de la zona horaria del navegador.
          fechaInicio: fechaInicio ? fechaInicio.format("YYYY-MM-DD") : null,
          fechaFin: fechaFin ? fechaFin.format("YYYY-MM-DD") : null,
          tipoPeriodoId: values.tipoPeriodoId || null,
        },
        { headers: this.props.headersToken }
      )
      .then((res) => {
        if (res.data.success === 2) {
          const prenominas = res.data.prenominas || [];
          this.setState({
            loading: false,
            consultado: true,
            prenominas,
            seleccionadas: [],
          });
          if (prenominas.length === 0) {
            message.info("No hay prenóminas con fecha de pago en ese rango");
          }
        } else if (res.data.success === 1) {
          message.warning(res.data.message, 8);
          this.setState({ loading: false });
        } else {
          message.error("Ha sucedido un error y no se cargaron las prenóminas");
          this.setState({ loading: false });
        }
      })
      .catch(() => {
        message.error("Ha sucedido un error y no se cargaron las prenóminas");
        this.setState({ loading: false });
      });
  }

  // ── Paso 2: bajar el detalle y armar el Excel ─────────────────────────────
  async generarExcel() {
    const {
      seleccionadas, hojasPorPrenomina, concentrado, concentradoEmpleados, bajas, vacaciones,
      filtroFiniquito, datosEmpleado,
    } = this.state;

    if (seleccionadas.length === 0) {
      return message.warning("Selecciona al menos una prenómina");
    }
    if (!hojasPorPrenomina && !concentrado && !concentradoEmpleados && !bajas && !vacaciones) {
      return message.warning("Selecciona al menos una hoja para el reporte");
    }

    const valores = this.formRef.current ? this.formRef.current.getFieldsValue() : {};

    this.setState({ generando: true });
    try {
      const res = await axios.post(
        "reportesNomina/reporteListaRaya",
        {
          empresaId: this.props.empresaId,
          prenominaIds: seleccionadas,
          registrosPatronales: valores.registrosPatronales || [],
          departamentos: valores.departamentos || [],
          incluirBajas: bajas,
          incluirVacaciones: vacaciones,
        },
        { headers: this.props.headersToken }
      );

      if (res.data.success === 1) {
        // Tope de filas o filtro sin resultados: el backend dice cómo acotar.
        message.warning(res.data.message, 8);
        return;
      }
      if (res.data.success !== 2) {
        return message.error("Ha sucedido un error y no se generó el reporte");
      }

      await descargarListaRaya({
        prenominas: res.data.prenominas || [],
        noSumaIds: new Set(res.data.conceptosNoSuma || []),
        especieIds: new Set(res.data.conceptosEspecie || []),
        opciones: {
          hojasPorPrenomina, concentrado, concentradoEmpleados, bajas, vacaciones,
          filtroFiniquito, datosEmpleado,
        },
      });
    } catch (error) {
      message.error("Ha sucedido un error y no se generó el reporte");
    } finally {
      this.setState({ generando: false });
    }
  }

  render() {
    const {
      loading, generando, consultado, prenominas, seleccionadas,
      hojasPorPrenomina, concentrado, concentradoEmpleados, bajas, vacaciones, filtroFiniquito,
      datosEmpleado,
    } = this.state;

    const columnas = [
      {
        title: "Periodo",
        dataIndex: "periodoNumero",
        width: 90,
        render: (v) => (v == null ? "—" : v),
      },
      { title: "Nombre", dataIndex: "nombre" },
      { title: "Tipo", dataIndex: "tipoPeriodoNombre", width: 120 },
      {
        title: "Del",
        dataIndex: "fechaInicio",
        width: 110,
        align: "center",
        render: (v) => (v ? moment.utc(v).format("DD/MM/YYYY") : "—"),
      },
      {
        title: "Al",
        dataIndex: "fechaFin",
        width: 110,
        align: "center",
        render: (v) => (v ? moment.utc(v).format("DD/MM/YYYY") : "—"),
      },
      {
        title: "Fecha de pago",
        dataIndex: "fechaPago",
        width: 120,
        align: "center",
        render: (v) => (v ? moment.utc(v).format("DD/MM/YYYY") : "—"),
      },
      {
        title: "Estatus",
        dataIndex: "estatus",
        width: 150,
        render: (v) => (
          <Tag color={COLOR_ESTATUS[v] || "default"}>{(v || "").replace(/_/g, " ")}</Tag>
        ),
      },
      { title: "Empleados", dataIndex: "empleados", width: 100, align: "right" },
      {
        title: "Total neto",
        dataIndex: "totalNeto",
        width: 130,
        align: "right",
        render: (v) => dineroDisabledMask(v || 0),
      },
    ];

    return (
      <>
        <Form
          ref={this.formRef}
          name="reporteListaRaya"
          onFinish={this.consultarPrenominas.bind(this)}
          onFinishFailed={this.onFinishFailed}
          initialValues={{
            periodo: [moment().startOf("month"), moment().endOf("month")],
          }}
        >
          <PageHeader
            onBack={this.atras.bind(this)}
            title="Lista de Raya"
            style={{
              border: "1px solid rgb(235, 237, 240)",
              backgroundColor: "white",
              marginBottom: 10,
            }}
            extra={
              <div style={{ display: "flex", justifyContent: "right" }}>
                <Space>
                  {seleccionadas.length > 0 && (
                    <ButtonItem
                      blanco
                      loading={generando}
                      onClick={() => this.generarExcel()}
                      icon={<FileExcelOutlined />}
                      style={{ display: "block", margin: "0 0 0 auto" }}
                    >
                      Descargar Excel ({seleccionadas.length})
                    </ButtonItem>
                  )}
                  <ButtonItem
                    type="primary"
                    htmlType="submit"
                    style={{ display: "block", margin: "0 0 0 auto" }}
                  >
                    Consultar
                  </ButtonItem>
                </Space>
              </div>
            }
          />
          <Spin spinning={loading || generando}>
            <div
              style={{
                backgroundColor: "white",
                padding: 20,
                paddingTop: 5,
                borderRadius: 5,
                boxShadow: "0 4px 14px rgba(0,0,0,0.08)",
                marginBottom: 10,
              }}
            >
              {/* forceRender en todas: sin él se pierden los campos de las
                  pestañas que el usuario no llegó a abrir. */}
              <Tabs
                style={{ margin: 0 }}
                tabBarExtraContent={{
                  left: <h1 style={{ padding: 0, margin: "0 10px 0 0" }}>Filtros:</h1>,
                }}
              >
                <TabPane tabKey="1" key="1" forceRender tab="Fecha de pago">
                  <FormItem name="periodo" required noStyle>
                    <RangePickerItem />
                  </FormItem>
                </TabPane>
                <TabPane tabKey="2" key="2" forceRender tab="Tipo de periodo">
                  <FormItem name="tipoPeriodoId" noStyle margin>
                    <SelectItem width="90%" allowClear placeholder="Todos los tipos de periodo">
                      {this.state.objetoTiposPeriodo}
                    </SelectItem>
                  </FormItem>
                </TabPane>
                <TabPane tabKey="3" key="3" forceRender tab="Registro Patronal">
                  <FormItem name="registrosPatronales" noStyle margin>
                    <SelectItem
                      width="90%"
                      mode="multiple"
                      placeholder="Todos los registros patronales"
                    >
                      {this.state.objetoRegistrosPatronales}
                    </SelectItem>
                  </FormItem>
                </TabPane>
                <TabPane tabKey="4" key="4" forceRender tab="Departamentos">
                  <FormItem name="departamentos" noStyle margin>
                    <SelectDepartamentosItem
                      mode="multiple"
                      empresaId={this.props.empresaId}
                      headersToken={this.props.headersToken}
                      placeholder="Todos los departamentos"
                      style={{ width: "90%" }}
                    />
                  </FormItem>
                </TabPane>
              </Tabs>
              <p style={{ margin: "8px 0 0", fontSize: 12, color: "#999" }}>
                El registro patronal y los departamentos acotan los empleados que salen en el
                Excel; el tipo de periodo sólo filtra la lista de abajo.
              </p>
            </div>

            {consultado && (
              <div
                style={{
                  backgroundColor: "white",
                  padding: 20,
                  paddingTop: 10,
                  marginBottom: 15,
                  borderRadius: 5,
                  boxShadow: "0 4px 14px rgba(0,0,0,0.08)",
                }}
              >
                <Space wrap style={{ marginBottom: 12 }}>
                  <span style={{ color: "#666" }}>Hojas del archivo:</span>
                  <Checkbox
                    checked={hojasPorPrenomina}
                    onChange={(e) => this.setState({ hojasPorPrenomina: e.target.checked })}
                  >
                    Una hoja por prenómina
                  </Checkbox>
                  <Checkbox
                    checked={concentrado}
                    onChange={(e) => this.setState({ concentrado: e.target.checked })}
                  >
                    Concentrado por periodo
                  </Checkbox>
                  <Checkbox
                    checked={concentradoEmpleados}
                    onChange={(e) => this.setState({ concentradoEmpleados: e.target.checked })}
                  >
                    Concentrado por empleado
                  </Checkbox>
                  <Checkbox
                    checked={bajas}
                    onChange={(e) => this.setState({ bajas: e.target.checked })}
                  >
                    Bajas del periodo
                  </Checkbox>
                  <Checkbox
                    checked={vacaciones}
                    onChange={(e) => this.setState({ vacaciones: e.target.checked })}
                  >
                    Vacaciones
                  </Checkbox>
                </Space>

                {/* Las tres columnas entran justo después de "Empleado" y sólo en las hojas
                    de detalle por empleado (Lista de Raya y Concentrado por empleado); los
                    concentrados por departamento o periodo no tienen renglón por empleado.
                    Apagado por omisión: son datos personales y el libro se manda por correo. */}
                <Space wrap style={{ marginBottom: 12 }}>
                  <span style={{ color: "#666" }}>Columnas:</span>
                  <Checkbox
                    checked={datosEmpleado}
                    onChange={(e) => this.setState({ datosEmpleado: e.target.checked })}
                  >
                    Datos del empleado (RFC, NSS, CURP)
                  </Checkbox>
                </Space>

                {/* Los finiquitos se pagan DENTRO de la prenómina ordinaria, así que una
                    liquidación puede valer varias veces la raya del periodo. El filtro corre
                    antes del catálogo de conceptos: en "sin finiquitos" desaparecen también las
                    columnas que sólo existen por ellos (indemnización, prima de antigüedad,
                    ISR de separación), en vez de quedar en cero. */}
                <Space wrap style={{ marginBottom: 12 }}>
                  <span style={{ color: "#666" }}>Finiquitos:</span>
                  <Select
                    value={filtroFiniquito}
                    onChange={(v) => this.setState({ filtroFiniquito: v })}
                    style={{ width: 240 }}
                  >
                    <Option value="sin">
                      <span style={{ fontWeight: 600, color: "#00BFD6" }}>
                        Ordinaria (sin finiquitos)
                      </span>
                    </Option>
                    <Option value="todos">Completa (con finiquitos)</Option>
                    <Option value="solo">Sólo finiquitos</Option>
                  </Select>
                </Space>

                <p style={{ margin: "0 0 12px", fontSize: 12, color: "#999" }}>
                  El <b>concentrado por periodo</b> lleva un renglón por periodo con su desglose
                  por departamento. El <b>concentrado por empleado</b> es la lista de raya de
                  siempre, pero con cada empleado una sola vez y sus importes sumados a lo largo
                  de los periodos seleccionados.
                </p>

                <p style={{ marginBottom: 10, color: "#666" }}>
                  {prenominas.length} prenómina(s) en el rango
                  {seleccionadas.length > 0 && ` · ${seleccionadas.length} seleccionada(s)`}
                </p>

                <Table
                  size="small"
                  rowKey="_id"
                  dataSource={prenominas}
                  columns={columnas}
                  pagination={{ defaultPageSize: 15 }}
                  scroll={{ x: 1100 }}
                  rowSelection={{
                    selectedRowKeys: seleccionadas,
                    onChange: (keys) => this.setState({ seleccionadas: keys }),
                    getCheckboxProps: (record) => ({ disabled: record.empleados === 0 }),
                  }}
                />
              </div>
            )}
          </Spin>
        </Form>
      </>
    );
  }
}

const mapStateToProps = (state) => {
  return {
    headersToken: state.user.headersToken,
    empresaId: state.empresa.id,
  };
};

export default connect(mapStateToProps)(ReporteListaRaya);
